import React, { useState, useEffect } from 'react';
import { apiConfig, buildApiUrl } from '../config/api';

interface MembershipCriteria {
  id: number;
  membership_type: string;
  criteria: string;
  priority_order: number;
  description: string;
  created_at: string;
  updated_at: string;
}

interface CriteriaConfig {
  leadership_count?: { min: number };
  completed_discover_grace?: boolean;
  group_count?: { min: number };
  recent_checkins?: { min: number };
  completed_first_time_visitor?: boolean;
  default?: boolean;
}

const MembershipCriteriaManagement: React.FC = () => {
  const [criteria, setCriteria] = useState<MembershipCriteria[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    membership_type: '',
    criteria: {} as CriteriaConfig,
    priority_order: 1,
    description: ''
  });

  useEffect(() => {
    fetchCriteria();
  }, []);

  const fetchCriteria = async () => {
    try {
      setLoading(true);
      const response = await fetch(buildApiUrl(apiConfig.endpoints.admin.membershipCriteria), {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch criteria');
      }
      
      const data = await response.json();
      setCriteria(data.criteria || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const url = editingId 
        ? buildApiUrl(apiConfig.endpoints.admin.membershipCriteria)
        : buildApiUrl(apiConfig.endpoints.admin.membershipCriteria);
      
      const method = editingId ? 'PUT' : 'POST';
      const body = editingId 
        ? { id: editingId, ...formData }
        : formData;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        throw new Error('Failed to save criteria');
      }

      await fetchCriteria();
      setEditingId(null);
      setShowAddForm(false);
      resetForm();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this criteria?')) return;

    try {
      const response = await fetch(
        `${buildApiUrl(apiConfig.endpoints.admin.membershipCriteria)}?id=${id}`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete criteria');
      }

      await fetchCriteria();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleEdit = (item: MembershipCriteria) => {
    setEditingId(item.id);
    setFormData({
      membership_type: item.membership_type,
      criteria: JSON.parse(item.criteria),
      priority_order: item.priority_order,
      description: item.description
    });
  };

  const resetForm = () => {
    setFormData({
      membership_type: '',
      criteria: {},
      priority_order: 1,
      description: ''
    });
  };

  const updateCriteriaField = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      criteria: {
        ...prev.criteria,
        [field]: value
      }
    }));
  };

  const renderCriteriaForm = () => (
    <div className="bg-white p-6 rounded-lg shadow-md mb-6">
      <h3 className="text-lg font-semibold mb-4">
        {editingId ? 'Edit Membership Criteria' : 'Add New Membership Criteria'}
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Membership Type
          </label>
          <input
            type="text"
            value={formData.membership_type}
            onChange={(e) => setFormData(prev => ({ ...prev, membership_type: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., Leader, Member, etc."
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Priority Order
          </label>
          <input
            type="number"
            value={formData.priority_order}
            onChange={(e) => setFormData(prev => ({ ...prev, priority_order: parseInt(e.target.value) }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            min="1"
          />
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows={2}
          placeholder="Description of this membership type..."
        />
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Criteria Configuration
        </label>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Leadership Count (min)</label>
            <input
              type="number"
              value={formData.criteria.leadership_count?.min || ''}
              onChange={(e) => updateCriteriaField('leadership_count', { min: parseInt(e.target.value) || 0 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="0"
            />
          </div>
          
          <div>
            <label className="block text-sm text-gray-600 mb-1">Group Count (min)</label>
            <input
              type="number"
              value={formData.criteria.group_count?.min || ''}
              onChange={(e) => updateCriteriaField('group_count', { min: parseInt(e.target.value) || 0 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="0"
            />
          </div>
          
          <div>
            <label className="block text-sm text-gray-600 mb-1">Recent Check-ins (min)</label>
            <input
              type="number"
              value={formData.criteria.recent_checkins?.min || ''}
              onChange={(e) => updateCriteriaField('recent_checkins', { min: parseInt(e.target.value) || 0 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="0"
            />
          </div>
        </div>

        <div className="mt-4 space-y-2">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.criteria.completed_discover_grace || false}
              onChange={(e) => updateCriteriaField('completed_discover_grace', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm text-gray-600">Completed Discover Grace Class</span>
          </label>
          
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.criteria.completed_first_time_visitor || false}
              onChange={(e) => updateCriteriaField('completed_first_time_visitor', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm text-gray-600">Completed First Time Visitor</span>
          </label>
          
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.criteria.default || false}
              onChange={(e) => updateCriteriaField('default', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm text-gray-600">Default (catch-all)</span>
          </label>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={handleSave}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {editingId ? 'Update' : 'Create'}
        </button>
        <button
          onClick={() => {
            setEditingId(null);
            setShowAddForm(false);
            resetForm();
          }}
          className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Membership Criteria Management</h1>
        <button
          onClick={() => setShowAddForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          + Add New Criteria
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {(showAddForm || editingId) && renderCriteriaForm()}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Current Membership Criteria</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Membership Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Criteria
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {criteria.map((item) => (
                <tr key={item.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {item.membership_type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {item.priority_order}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {item.description}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <pre className="text-xs bg-gray-100 p-2 rounded">
                      {JSON.stringify(JSON.parse(item.criteria), null, 2)}
                    </pre>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEdit(item)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MembershipCriteriaManagement;
